"use client";

import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from "react";
import { createPortal } from "react-dom";
import dynamic from "next/dynamic";
import { X } from "lucide-react";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@workspace/ui/components/dialog";
import { cn } from "@workspace/ui/lib/utils";

import { spineFor } from "@/lib/books/shelf";
import { BookFacts } from "./book-details";
import { CoverArt, type ShelfBook } from "./book-parts";
import { garamond } from "./fonts";
import { SceneBoundary } from "./scene/scene-boundary";
import type { Frame, Rect } from "./scene/stage";
import type { ShelfLook } from "./shelf-look";
import { THEME_STYLES, type Camera } from "./shelf-themes";

const load = () => import("./scene/inspector-scene");
const InspectorScene = dynamic(load, { ssr: false });

/** Fetches the scene ahead of the first click, so the book is not missing from the shelf while it loads. */
export function preloadInspector() {
  void load();
}

export type Inspection = {
  book: ShelfBook;
  /** The book's place on the shelf, in viewport pixels. */
  from: Rect;
  /** The book was lying in a pile, so `from` is its spine on its side. */
  lying: boolean;
  /** Standing cover out at this spine thickness, so `from` is its front cover. */
  facedThickness: number | null;
  /** The shelf's camera, where the shelf is drawn in perspective. */
  camera: Camera | null;
  /** The book is on its way back to the shelf. */
  returning: boolean;
};

/**
 * Where the book is shown, in viewport pixels: its centre, its height and its
 * scale from the shelf; and, opened, where its centre moves to and how it is
 * scaled down so both halves fit.
 */
export type Stage = { x: number; y: number; height: number; scale: number; open: { x: number; scale: number } };

/** How the reader has turned the book, in radians, how fast it is still turning, and whether it is open. */
export type Spin = { yaw: number; pitch: number; velocity: number; dragging: boolean; open: boolean };

// Front covers are a 2:3 trim.
const COVER_RATIO = 0.66;
const WIDE = 768;
/** Radians turned per pixel dragged. */
const TURN_PER_PIXEL = 0.009;
const MAX_PITCH = 1.1;

function stageFor(viewport: Frame, from: Rect, lying: boolean): Stage {
  const { width, height } = viewport;
  const wide = width >= WIDE;
  const x = wide ? width * 0.27 : width / 2;
  const y = wide ? height / 2 : height * 0.3;
  const shown = wide
    ? Math.min(height * 0.66, (width * 0.34) / COVER_RATIO)
    : Math.min(height * 0.4, (width * 0.56) / COVER_RATIO);
  const scale = shown / (lying ? from.width : from.height);
  // Open, the front board swings out to the left of the spine, so the book moves over to stay in the middle.
  const smaller = wide ? 0.82 : 0.72;
  return { x, y, height: shown, scale, open: { x: x - shown * COVER_RATIO * smaller * 0.45, scale: scale * smaller } };
}

/**
 * A book taken down from the shelf: it flies to the left half of the screen,
 * scaled up, where it can be turned by dragging, with its details on the
 * right. On phones the book sits above its details.
 *
 * The book is drawn on a canvas over the whole viewport, outside the dialog,
 * so it can fly back to the shelf after the dialog has closed.
 */
export function BookInspector({
  inspection,
  look,
  onClose,
  onReturned,
}: {
  inspection: Inspection;
  look: ShelfLook;
  onClose: () => void;
  onReturned: () => void;
}) {
  const { book, from, lying, facedThickness, camera, returning } = inspection;
  const style = THEME_STYLES[look.theme];
  const viewport = useViewport();
  const stage = stageFor(viewport, from, lying);
  const spin = useRef<Spin>({ yaw: 0, pitch: 0, velocity: 0, dragging: false, open: false });
  const [flat, setFlat] = useState(false);
  const [open, setOpen] = useState(false);
  const wide = viewport.width >= WIDE;

  useEffect(() => {
    spin.current = { yaw: 0, pitch: 0, velocity: 0, dragging: false, open: false };
    setOpen(false);
  }, [book.id]);
  useEffect(() => {
    spin.current.open = open;
  }, [open]);
  const toggle = useCallback(() => setOpen((current) => !current), []);

  // Without WebGL there is no flight back to wait for.
  useEffect(() => {
    if (flat && returning) onReturned();
  }, [flat, returning, onReturned]);

  return (
    <>
      {!flat &&
        createPortal(
          <div aria-hidden className="pointer-events-none fixed inset-0 z-[60]">
            <SceneBoundary onError={() => setFlat(true)}>
              <InspectorScene
                key={book.id}
                book={book}
                from={from}
                lying={lying}
                facedThickness={facedThickness}
                stage={stage}
                viewport={viewport}
                camera={camera}
                look={look}
                returning={returning}
                spin={spin}
                onReturned={onReturned}
              />
            </SceneBoundary>
          </div>,
          document.body,
        )}
      <Dialog open={!returning} onOpenChange={(open) => !open && onClose()}>
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          className={cn(
            style.font === "serif" ? garamond.className : "font-sans",
            style.surface,
            "top-0 left-0 block h-svh w-screen max-w-none translate-x-0 translate-y-0 rounded-none border-0 p-0 shadow-none backdrop-blur-[6px] sm:max-w-none",
          )}
          // The backdrop around the book and its details puts it back.
          onClick={(event) => event.target === event.currentTarget && onClose()}
        >
          <Turntable book={book} stage={stage} spin={spin} open={open} onToggle={flat ? undefined : toggle} />
          {flat && <FlatCover book={book} stage={stage} />}
          <div
            className={cn(
              "pointer-events-none absolute flex",
              wide ? "inset-y-0 right-0 w-1/2 items-center pr-[8vw]" : "inset-x-0 bottom-0 top-[56%] px-7",
            )}
          >
            <div
              className={cn(
                "pointer-events-auto max-w-lg space-y-3 text-lg duration-700 animate-in fade-in fill-mode-both",
                wide ? "slide-in-from-right-6 delay-200" : "slide-in-from-bottom-4 delay-150",
              )}
            >
              {book.series && (
                <p className={cn("text-sm tracking-[0.16em] uppercase", style.muted)}>{book.series}</p>
              )}
              <DialogTitle
                className={cn(
                  "text-balance text-4xl leading-[1.05] sm:text-6xl",
                  style.font === "serif" ? "font-medium tracking-[-0.01em]" : "font-semibold tracking-[-0.03em]",
                )}
              >
                {book.title}
              </DialogTitle>
              <p className="text-xl">{book.author}</p>
              <div className="space-y-2 pt-3">
                <BookFacts book={book} muted={style.muted} />
              </div>
              {!flat && (
                <button
                  type="button"
                  onClick={toggle}
                  className={cn(
                    "mt-2 rounded-full border border-current/25 px-4 py-1.5 text-sm transition outline-none hover:bg-current/10 focus-visible:ring-2 focus-visible:ring-current/40",
                  )}
                >
                  {open ? "Close it" : "Open it"}
                </button>
              )}
            </div>
          </div>
          <DialogClose className="absolute top-4 right-4 rounded-full p-2 opacity-70 transition outline-none hover:bg-current/10 hover:opacity-100 focus-visible:ring-2 focus-visible:ring-current/40">
            <X className="size-5" />
            <span className="sr-only">Put the book back</span>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * The area over the book that turns it: drag to turn it and let go to let it
 * spin on, or use the arrow keys. A tap, or Enter, opens and closes it.
 */
function Turntable({
  book,
  stage,
  spin,
  open,
  onToggle,
}: {
  book: ShelfBook;
  stage: Stage;
  spin: RefObject<Spin>;
  open: boolean;
  onToggle?: () => void;
}) {
  const last = useRef<{ x: number; y: number; time: number } | null>(null);
  const pressed = useRef<{ x: number; y: number; time: number } | null>(null);
  const width = stage.height * (open ? 1.6 : 0.9);
  const height = stage.height * 1.1;
  const x = open ? stage.open.x : stage.x;

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    last.current = { x: event.clientX, y: event.clientY, time: event.timeStamp };
    pressed.current = last.current;
    spin.current.dragging = true;
    spin.current.velocity = 0;
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const from = last.current;
    if (!from) return;
    const s = spin.current;
    const yaw = (event.clientX - from.x) * TURN_PER_PIXEL;
    s.yaw += yaw;
    s.pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, s.pitch + (event.clientY - from.y) * TURN_PER_PIXEL));
    s.velocity = yaw / Math.max(0.008, (event.timeStamp - from.time) / 1000);
    last.current = { x: event.clientX, y: event.clientY, time: event.timeStamp };
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    // A pause before letting go stops the book rather than flinging it.
    if (last.current && event.timeStamp - last.current.time > 80) spin.current.velocity = 0;
    const down = pressed.current;
    const tapped =
      down &&
      event.type === "pointerup" &&
      Math.hypot(event.clientX - down.x, event.clientY - down.y) < 6 &&
      event.timeStamp - down.time < 400;
    last.current = null;
    pressed.current = null;
    spin.current.dragging = false;
    if (tapped) onToggle?.();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const s = spin.current;
    if (event.key === "Enter" || event.key === " ") onToggle?.();
    else if (event.key === "ArrowLeft") s.velocity -= 3;
    else if (event.key === "ArrowRight") s.velocity += 3;
    else if (event.key === "ArrowUp") s.pitch = Math.max(-MAX_PITCH, s.pitch - 0.3);
    else if (event.key === "ArrowDown") s.pitch = Math.min(MAX_PITCH, s.pitch + 0.3);
    else return;
    event.preventDefault();
  };

  return (
    <div
      role="img"
      tabIndex={0}
      aria-label={`${book.title}. Drag or use the arrow keys to turn it; tap or press Enter to ${open ? "close" : "open"} it.`}
      className="absolute cursor-grab touch-none rounded-lg outline-none select-none focus-visible:ring-2 focus-visible:ring-current/30 active:cursor-grabbing"
      style={{ left: x - width / 2, top: stage.y - height / 2, width, height }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
    />
  );
}

/** The cover alone, where WebGL is unavailable. */
function FlatCover({ book, stage }: { book: ShelfBook; stage: Stage }) {
  const width = stage.height * COVER_RATIO;
  return (
    <span
      className="pointer-events-none absolute overflow-hidden rounded-[2px_5px_5px_2px] text-[26px] shadow-[0_40px_80px_-30px_rgb(0_0_0/0.9)] duration-500 animate-in fade-in zoom-in-95"
      style={{ left: stage.x - width / 2, top: stage.y - stage.height / 2, width, height: stage.height }}
    >
      <CoverArt book={book} spine={spineFor(book)} sizes={`${Math.round(width)}px`} eager />
    </span>
  );
}

function useViewport(): Frame {
  const [viewport, setViewport] = useState<Frame>(() => ({ width: window.innerWidth, height: window.innerHeight }));
  useEffect(() => {
    const resize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);
  return viewport;
}
